import { portLabel } from "@/lib/portAreaMatchUtils";
import type { PortAreaExpansionResponse } from "@/types/api";

import type { PortSuggestOption } from "@/components/common/PortSuggestInput";

export interface PortSuggestAreaGroup {
  id: string;
  /** Short heading, e.g. "Ningbo area". */
  title: string;
  /** Human-readable list of all ports in the mapped area. */
  mappedSummary: string;
  ports: PortSuggestOption[];
  /** Every LOCODE in the mapped area (for multi-port filter tokens). */
  memberLocodes: string[];
}

export interface PortSuggestMenuStructure {
  standalone: PortSuggestOption[];
  areaGroups: PortSuggestAreaGroup[];
  /** Flat list in render order (for keyboard highlight). */
  flat: PortSuggestOption[];
}

const upper = (s: string) => s.trim().toUpperCase();

/** Stable key for an area — same siblings share one group in the menu. */
function memberSetKey(members: { locode: string }[]): string {
  return [...members.map((m) => upper(m.locode))].sort().join(",");
}

function areaGroupTitle(members: { locode: string; name: string | null }[]): string {
  const sorted = [...members].sort((a, b) =>
    upper(a.locode).localeCompare(upper(b.locode)),
  );
  const primary = sorted[0];
  const name = primary?.name?.trim();
  if (name) return `${name} area`;
  if (primary?.locode) return `Port area ${upper(primary.locode)}`;
  return "Mapped port area";
}

/**
 * Split ranked search hits into standalone ports and multi-port area groups
 * using `GET /v1/ports/area-expansion`.
 */
export function buildPortSuggestMenuStructure(
  options: PortSuggestOption[],
  expansion: PortAreaExpansionResponse | null,
): PortSuggestMenuStructure {
  const assigned = new Set<string>();
  const areaGroups: PortSuggestAreaGroup[] = [];
  const seenMemberSets = new Set<string>();

  for (const exp of expansion?.expansions ?? []) {
    if (exp.members.length <= 1) continue;

    const setKey = memberSetKey(exp.members);
    if (seenMemberSets.has(setKey)) continue;
    seenMemberSets.add(setKey);

    const memberSet = new Set(exp.members.map((m) => upper(m.locode)));
    const inMenu = options.filter((o) => memberSet.has(upper(o.locode)));
    if (inMenu.length === 0) continue;

    for (const p of inMenu) assigned.add(upper(p.locode));

    const mappedSummary = [...exp.members]
      .sort((a, b) => upper(a.locode).localeCompare(upper(b.locode)))
      .map((m) => portLabel(upper(m.locode), m.name))
      .join(" · ");

    areaGroups.push({
      id: setKey,
      title: areaGroupTitle(exp.members),
      mappedSummary,
      ports: inMenu,
      memberLocodes: [...exp.members.map((m) => upper(m.locode))].sort(),
    });
  }

  const standalone = options.filter((o) => !assigned.has(upper(o.locode)));
  const flat = [...standalone, ...areaGroups.flatMap((g) => g.ports)];

  return { standalone, areaGroups, flat };
}
